// DomainsPage — list all sending domains for the tenant.
// Each row shows FQDN, last compliance grade, last score, and last scan time.
// Clicking a row navigates to /domains/:domainId/scans.

import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Globe, ChevronRight, Plus } from "lucide-react";
import { fetchDomains, type Domain } from "../api";
import { ErrorState } from "@avagency/frontend/components/shell/ErrorState";
import { LoadingState } from "@avagency/frontend/components/shell/LoadingState";
import { Footer } from "../components/Footer";

// Grade background (pill badge) colors
const GRADE_BG: Record<string, { bg: string; border: string; color: string }> = {
  A: { bg: "rgba(155,227,177,0.10)", border: "rgba(155,227,177,0.25)", color: "#9be3b1" },
  B: { bg: "rgba(132,204,22,0.08)", border: "rgba(132,204,22,0.22)", color: "#84cc16" },
  C: { bg: "rgba(250,204,21,0.08)", border: "rgba(250,204,21,0.20)", color: "#facc15" },
  D: { bg: "rgba(251,146,60,0.08)", border: "rgba(251,146,60,0.20)", color: "#fb923c" },
  F: { bg: "rgba(255,138,138,0.08)", border: "rgba(255,138,138,0.20)", color: "#ff8a8a" },
};

function GradePill({ grade }: { grade: string }) {
  if (!grade) {
    return (
      <span style={{ color: "var(--yg-silver-3)", fontSize: "12px" }}>—</span>
    );
  }
  const style = GRADE_BG[grade];
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold font-mono"
      style={{
        background: style?.bg ?? "transparent",
        border: `1px solid ${style?.border ?? "transparent"}`,
        color: style?.color ?? "inherit",
        letterSpacing: "0.06em",
      }}
      aria-label={`Grade ${grade}`}
    >
      {grade}
    </span>
  );
}

function DomainRow({ domain }: { domain: Domain }) {
  const scannedAt = domain.last_scanned_at
    ? new Date(domain.last_scanned_at).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "Never";

  return (
    <Link
      to={`/domains/${domain.id}/scans`}
      className="yg-domain-row"
      aria-label={`${domain.fqdn}, grade ${domain.last_grade || "unknown"}, view scan history`}
    >
      {/* Domain icon */}
      <div
        className="flex h-9 w-9 items-center justify-center rounded-lg flex-shrink-0"
        style={{
          background: "rgba(96,165,250,0.08)",
          border: "1px solid rgba(96,165,250,0.15)",
          color: "var(--yg-blue)",
        }}
        aria-hidden="true"
      >
        <Globe className="h-4 w-4" />
      </div>

      {/* FQDN */}
      <span
        className="min-w-0 flex-1 truncate font-medium text-sm"
        style={{ color: "var(--yg-silver-0)", letterSpacing: "-0.01em" }}
      >
        {domain.fqdn}
      </span>

      {/* Meta strip */}
      <div className="hidden sm:flex items-center gap-5 flex-shrink-0">
        <div className="text-right">
          <div
            className="text-xs"
            style={{ color: "var(--yg-silver-3)", fontFamily: "var(--yg-font-mono)", letterSpacing: "0.06em", textTransform: "uppercase" }}
          >
            Score
          </div>
          <div
            className="text-sm font-semibold"
            style={{ color: "var(--yg-silver-0)" }}
          >
            {domain.last_score ?? "—"}
          </div>
        </div>
        <div className="text-right">
          <div
            className="text-xs mb-1"
            style={{ color: "var(--yg-silver-3)", fontFamily: "var(--yg-font-mono)", letterSpacing: "0.06em", textTransform: "uppercase" }}
          >
            Grade
          </div>
          <GradePill grade={domain.last_grade} />
        </div>
        <div className="text-right w-28 hidden md:block">
          <div
            className="text-xs"
            style={{ color: "var(--yg-silver-3)", fontFamily: "var(--yg-font-mono)", letterSpacing: "0.06em", textTransform: "uppercase" }}
          >
            Last scan
          </div>
          <div
            className="text-xs mt-0.5"
            style={{ color: "var(--yg-silver-2)" }}
          >
            {scannedAt}
          </div>
        </div>
      </div>

      {/* Mobile grade badge */}
      <div className="flex sm:hidden">
        <GradePill grade={domain.last_grade} />
      </div>

      <ChevronRight
        className="h-4 w-4 flex-shrink-0"
        style={{ color: "var(--yg-silver-3)" }}
        aria-hidden="true"
      />
    </Link>
  );
}

function DomainsEmpty() {
  return (
    <div className="yg-empty-state">
      <div className="yg-empty-icon" aria-hidden="true">
        <Globe className="h-6 w-6" />
      </div>
      <div className="yg-empty-title">No domains yet</div>
      <p className="yg-empty-body">
        Domains are imported automatically after your ESP integration syncs.
        Once connected, Yahoogle audits each sending domain for SPF, DKIM,
        and DMARC compliance.
      </p>
      <Link
        to="/connect"
        className="mt-6 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium"
        style={{
          background: "rgba(96,165,250,0.10)",
          border: "1px solid rgba(96,165,250,0.22)",
          color: "var(--yg-blue)",
          transition: "background 0.2s, border-color 0.2s",
          textDecoration: "none",
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLAnchorElement).style.background = "rgba(96,165,250,0.18)";
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLAnchorElement).style.background = "rgba(96,165,250,0.10)";
        }}
        aria-label="Connect an ESP"
      >
        <Plus className="h-4 w-4" aria-hidden="true" />
        Connect an ESP
      </Link>
    </div>
  );
}

export function DomainsPage() {
  const q = useQuery<Domain[]>({
    queryKey: ["yahoogle", "domains"],
    queryFn: () => fetchDomains(),
  });

  return (
    <div className="space-y-6">
      <div>
        <span className="yg-eyebrow">Sending infrastructure</span>
        <h1 className="yg-page-heading text-2xl">Domains</h1>
        <p className="yg-page-sub">
          Sending domains registered through your connected ESP.
        </p>
      </div>

      {q.isLoading && <LoadingState rows={4} />}
      {q.error && <ErrorState error={q.error} retry={() => q.refetch()} />}

      {q.data && q.data.length === 0 && <DomainsEmpty />}

      {q.data && q.data.length > 0 && (
        <div className="flex flex-col gap-2">
          {q.data.map((d) => (
            <DomainRow key={d.id} domain={d} />
          ))}
        </div>
      )}

      <Footer />
    </div>
  );
}
